import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { useState } from "react";

import { STRIPE_PUBLISHABLE_KEY } from "../env";

/**
 * Loaded once and reused for every mount (Stripe's own recommendation —
 * calling `loadStripe` repeatedly re-injects its script tag for no
 * benefit). `null` publishable key resolves to a `null` Stripe instance;
 * handled below as a (non-user-facing) configuration error.
 */
let stripePromise: Promise<Stripe | null> | undefined;
function getStripePromise(): Promise<Stripe | null> {
  if (!stripePromise) {
    stripePromise = STRIPE_PUBLISHABLE_KEY
      ? loadStripe(STRIPE_PUBLISHABLE_KEY)
      : Promise.resolve(null);
  }
  return stripePromise;
}

export interface StripeCheckoutFormState {
  /** The Stripe PaymentIntent client secret returned by `POST /subscriptions`. */
  clientSecret: string;
  planName: string;
  monthlyPriceEur: number;
  /** True while a previously-submitted payment is being confirmed. */
  isProcessing: boolean;
  errorMessage?: string;
  language: SupportedLanguage;
}

export type StripeCheckoutFormEvent =
  "submit-payment" | "cancel-payment" | "payment-succeeded" | "payment-failed";

export interface StripeCheckoutFormPayload {
  clientSecret?: string;
  message?: string;
}

/**
 * Embedded Stripe Elements checkout (#2797). Card data never touches this
 * component or any other application code — all input goes through
 * Stripe's own `PaymentElement` iframe, and we only ever see the
 * `clientSecret` the backend handed us and the outcome Stripe reports.
 *
 * Project-level (not a shared `@pimo/pimo-components` component): it wraps
 * `@stripe/react-stripe-js`, which is specific to this app's payment
 * integration, per the UX/UI design spec on #2785.
 */
export const StripeCheckoutForm: PimoReactComponent<
  StripeCheckoutFormState,
  StripeCheckoutFormEvent,
  StripeCheckoutFormPayload
> = ({
  fireEvent,
  clientSecret,
  planName,
  monthlyPriceEur,
  isProcessing,
  errorMessage,
  language,
}) => {
  return (
    <Elements
      stripe={getStripePromise()}
      options={{ clientSecret }}
      key={clientSecret}
    >
      <StripeCheckoutFormInner
        fireEvent={fireEvent}
        clientSecret={clientSecret}
        planName={planName}
        monthlyPriceEur={monthlyPriceEur}
        isProcessing={isProcessing}
        errorMessage={errorMessage}
        language={language}
      />
    </Elements>
  );
};

/**
 * Split out from `StripeCheckoutForm` because `useStripe`/`useElements`
 * only work inside an `<Elements>` provider — this is the component that
 * actually calls `stripe.confirmPayment`, which is why that call happens
 * here rather than in the plugin's event handler (the plugin has no access
 * to the `stripe`/`elements` instances; see `home-portal-plugin.ts`).
 */
const StripeCheckoutFormInner: PimoReactComponent<
  StripeCheckoutFormState,
  StripeCheckoutFormEvent,
  StripeCheckoutFormPayload
> = ({
  fireEvent,
  clientSecret,
  planName,
  monthlyPriceEur,
  isProcessing,
  errorMessage,
  language,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!stripe || !elements || isProcessing) return;

    fireEvent?.("submit-payment", { clientSecret });

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      // Embedded (no hosted redirect): Stripe only navigates away for
      // payment methods that require it. 3-D Secure/SCA for card payments
      // is handled inline, inside the Payment Element's own iframe — see
      // the UX/UI design spec comment on #2785.
      redirect: "if_required",
      confirmParams: { return_url: window.location.href },
    });

    if (error) {
      const message =
        error.type === "card_error" || error.type === "validation_error"
          ? (error.message ??
            getLocalizedMessage(
              language,
              "homePortalPage.selectPlan.payment.declined"
            ))
          : getLocalizedMessage(
              language,
              "homePortalPage.selectPlan.payment.genericError"
            );
      fireEvent?.("payment-failed", { message });
      return;
    }

    if (
      paymentIntent &&
      (paymentIntent.status === "succeeded" ||
        paymentIntent.status === "processing")
    ) {
      // Brief, visually-distinct success state (per UX spec: an inline
      // `Alert severity="success"` for ~1.5s is an accepted alternative to
      // a third dialog hop). Only once that has been shown do we tell the
      // plugin to close the modal and refresh subscription data — closing
      // immediately would cut the confirmation off before the user sees it.
      setShowSuccess(true);
      setTimeout(() => {
        fireEvent?.("payment-succeeded", {});
      }, 1500);
    }
  };

  if (showSuccess) {
    return (
      <Alert severity="success">
        {getLocalizedMessage(
          language,
          "homePortalPage.selectPlan.payment.success"
        )}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {getLocalizedMessage(
          language,
          "homePortalPage.selectPlan.payment.orderSummaryTitle"
        )}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {planName} —{" "}
        {getLocalizedMessage(
          language,
          "homePortalPage.selectPlan.payment.billedMonthly",
          { price: monthlyPriceEur }
        )}
      </Typography>

      <PaymentElement options={{ layout: "tabs" }} />

      {errorMessage ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      ) : null}

      <Button
        variant="contained"
        fullWidth
        disabled={!stripe || !elements || isProcessing}
        onClick={handleSubmit}
        sx={{
          mt: 2,
          bgcolor: "#ff8c00",
          color: "#fff",
          textTransform: "none",
          boxShadow: "none",
          "&:hover": { bgcolor: "#e67e00" },
          "&.Mui-disabled": { bgcolor: "#f0d4b3", color: "#fff" },
        }}
        startIcon={
          isProcessing ? (
            <CircularProgress size={16} color="inherit" />
          ) : undefined
        }
      >
        {isProcessing
          ? getLocalizedMessage(
              language,
              "homePortalPage.selectPlan.payment.submitting"
            )
          : getLocalizedMessage(
              language,
              "homePortalPage.selectPlan.payment.payButton"
            )}
      </Button>
    </Box>
  );
};
